import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { isHwbWellBeingCoachingServiceType } from '../../constants/productServiceTypes';
import { getAgeRangeFromAge, isValidAgeOneOrTwoDigits } from '../../utils/ageRange';
import { BRAND_COLORS } from '../../constants/branding';
import type { Specialist } from '../../types/phoneCapture';
import ServiceSpecialistAssignmentModal from './ServiceSpecialistAssignmentModal';

interface EmployeeSeedData {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  mobilePhone: string;
  age: string;
  gender: string;
}

export interface PatientCaptureData {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  mobilePhone: string;
  gender?: string;
  /** Edad numérica del paciente (años). Se guarda en Client.age. */
  age?: string;
  ageRange?: string;
  relationship: string;
  /** Texto libre cuando la relación es «Otro». */
  otherRelationshipDescription?: string;
  serviceType: string;
  /** Método de entrega del referral (catálogo DELIVERY_METHOD). */
  deliveryMethod: string;
  preDiagnosticNotes?: string;
  hasPriorPsychologicalTreatment?: boolean;
  pregnancyCoaching?: boolean | null;
  assignedSpecialistId?: number | null;
  assignedSpecialistName?: string | null;
  /** Fecha/hora de canalización del especialista. */
  assignmentDate?: string | null;
  /** IDs devueltos por PUT /phone-calls/{id}/draft (cliente / referral en borrador) */
  draftClientId?: number | null;
  draftReferralId?: number | null;
  /** Número de caso asignado por ComPsych después de sincronizar. */
  externalReferralId?: number | null;
  /** Liberado para cola de Sincronización ComPsych (irreversible). */
  readyForSync?: boolean;
}

interface CapturePatientModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PatientCaptureData) => void | Promise<void>;
  /** Solo consulta: mismos campos deshabilitados, sin guardar. */
  readOnly?: boolean;
  employeeData: EmployeeSeedData;
  serviceTypeOptions: Array<{ code: string; description: string }>;
  deliveryMethodOptions: Array<{ code: string; description: string }>;
  genderOptions: Array<{ code: string; description: string }>;
  ageRangeOptions: Array<{ code: string; description: string }>;
  existingPatients: PatientCaptureData[];
  initialData?: PatientCaptureData | null;
  validationError?: string;
  /** Fija el tipo de servicio (p. ej. crisis siempre EAP). */
  lockServiceType?: boolean;
}

const emptyPatientData: PatientCaptureData = {
  firstName: '',
  lastName: '',
  middleName: '',
  email: '',
  mobilePhone: '',
  gender: '',
  age: '',
  ageRange: '',
  relationship: '',
  otherRelationshipDescription: '',
  serviceType: '',
  deliveryMethod: '',
  preDiagnosticNotes: '',
  hasPriorPsychologicalTreatment: false,
  assignedSpecialistId: null,
  assignedSpecialistName: null,
  assignmentDate: null
};

const CapturePatientModal: React.FC<CapturePatientModalProps> = ({
  open,
  onClose,
  onSave,
  readOnly = false,
  employeeData,
  serviceTypeOptions,
  deliveryMethodOptions,
  genderOptions,
  ageRangeOptions,
  existingPatients,
  initialData,
  validationError,
  lockServiceType = false
}) => {
  const [formData, setFormData] = useState<PatientCaptureData>(emptyPatientData);
  const [selectedChildKey, setSelectedChildKey] = useState('NEW_CHILD');
  const [channelizationClearedWarning, setChannelizationClearedWarning] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const isReadOnly = Boolean(readOnly);
  const wasOpenRef = useRef(false);

  const normalize = (value: string) => (value || '').trim().toLowerCase();
  const personKey = (data: Pick<PatientCaptureData, 'firstName' | 'lastName' | 'middleName'>) =>
    `${normalize(data.firstName)}|${normalize(data.lastName)}|${normalize(data.middleName)}`;

  const childrenOptions = useMemo(() => {
    const seen = new Set<string>();
    return existingPatients
      .filter((item) => item.relationship === 'Child')
      .filter((item) => {
        const key = personKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [existingPatients]);

  const partnerOption = useMemo(
    () => existingPatients.find((item) => item.relationship === 'Partner') || null,
    [existingPatients]
  );

  const otherOptions = useMemo(
    () =>
      existingPatients.filter((item) => item.relationship === 'Other').filter((item, index, list) => {
        const key = personKey(item);
        return list.findIndex((entry) => personKey(entry) === key) === index;
      }),
    [existingPatients]
  );

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    const base: PatientCaptureData = initialData ? { ...initialData } : { ...emptyPatientData };
    if (base.relationship && base.relationship !== 'Employee' && !isReadOnly) {
      base.email = '';
    }
    setFormData(base);
    setSelectedChildKey(initialData && initialData.relationship === 'Child' ? personKey(initialData) : 'NEW_CHILD');
    setChannelizationClearedWarning(false);
    setAssignmentModalOpen(false);
  }, [open, initialData, isReadOnly]);

  const canSave = useMemo(() => {
    const otherRelationshipValid =
      formData.relationship !== 'Other' ||
      (formData.otherRelationshipDescription || '').trim() !== '';

    return (
      formData.relationship.trim() !== '' &&
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.serviceType.trim() !== '' &&
      formData.deliveryMethod.trim() !== '' &&
      otherRelationshipValid
    );
  }, [
    formData.relationship,
    formData.otherRelationshipDescription,
    formData.firstName,
    formData.lastName,
    formData.serviceType,
    formData.deliveryMethod
  ]);

  /** Datos del beneficiario vienen del empleado; solo tipo de servicio es editable en relación Employee. */
  const lockEmployeeIdentityFields = formData.relationship === 'Employee';
  const fieldDisabled = (identity: boolean) => isReadOnly || identity;

  const patientAgeError =
    !isReadOnly &&
    !lockEmployeeIdentityFields &&
    Boolean(formData.age) &&
    !isValidAgeOneOrTwoDigits(formData.age || '');

  const handleChange = (field: keyof PatientCaptureData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleServiceTypeChange = (nextServiceType: string) => {
    const previousType = (formData.serviceType || '').trim();
    const nextType = (nextServiceType || '').trim();
    const hasAssignedSpecialist =
      typeof formData.assignedSpecialistId === 'number' && formData.assignedSpecialistId > 0;

    if (hasAssignedSpecialist && previousType !== nextType) {
      setChannelizationClearedWarning(true);
      setFormData((prev) => ({
        ...prev,
        serviceType: nextServiceType,
        assignedSpecialistId: null,
        assignedSpecialistName: null,
        assignmentDate: null
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, serviceType: nextServiceType }));
  };

  useEffect(() => {
    if (formData.serviceType === 'EAP') return;
    if (!formData.hasPriorPsychologicalTreatment) return;
    setFormData((prev) => ({ ...prev, hasPriorPsychologicalTreatment: false }));
  }, [formData.serviceType, formData.hasPriorPsychologicalTreatment]);

  useEffect(() => {
    if (isHwbWellBeingCoachingServiceType(formData.serviceType)) {
      if (formData.pregnancyCoaching === undefined || formData.pregnancyCoaching === null) {
        setFormData((prev) => ({ ...prev, pregnancyCoaching: false }));
      }
      return;
    }
    if (formData.pregnancyCoaching != null) {
      setFormData((prev) => ({ ...prev, pregnancyCoaching: undefined }));
    }
  }, [formData.serviceType, formData.pregnancyCoaching]);

  const preloadPerson = (person: Partial<PatientCaptureData>, relationship: string) => {
    setFormData((prev) => ({
      ...prev,
      firstName: person.firstName || '',
      lastName: person.lastName || '',
      middleName: person.middleName || '',
      email: relationship === 'Employee' ? (person.email ?? '') : '',
      mobilePhone: person.mobilePhone || '',
      gender: person.gender || '',
      age: person.age || '',
      ageRange: person.ageRange || getAgeRangeFromAge(person.age || '', ageRangeOptions),
      relationship,
      otherRelationshipDescription:
        relationship === 'Other' ? (person.otherRelationshipDescription || '').trim() : ''
    }));
  };

  const clearPersonForRelationship = (relationship: string) => {
    setFormData((prev) => ({
      ...prev,
      firstName: '',
      lastName: '',
      middleName: '',
      email: '',
      mobilePhone: '',
      gender: '',
      age: '',
      ageRange: '',
      relationship,
      otherRelationshipDescription: ''
    }));
  };

  const handleRelationshipChange = (relationship: string) => {
    if (relationship === 'Employee') {
      const employeeAge = employeeData.age || '';
      preloadPerson(
        {
          firstName: employeeData.firstName,
          lastName: employeeData.lastName,
          middleName: employeeData.middleName,
          email: employeeData.email,
          mobilePhone: employeeData.mobilePhone,
          gender: employeeData.gender,
          age: employeeAge,
          ageRange: getAgeRangeFromAge(employeeAge, ageRangeOptions)
        },
        'Employee'
      );
      setSelectedChildKey('NEW_CHILD');
      return;
    }

    if (relationship === 'Partner') {
      if (partnerOption) {
        preloadPerson(partnerOption, 'Partner');
      } else {
        clearPersonForRelationship('Partner');
      }
      setSelectedChildKey('NEW_CHILD');
      return;
    }

    if (relationship === 'Child') {
      if (childrenOptions.length === 1) {
        preloadPerson(childrenOptions[0], 'Child');
        setSelectedChildKey(personKey(childrenOptions[0]));
      } else {
        clearPersonForRelationship('Child');
        setSelectedChildKey('NEW_CHILD');
      }
      return;
    }

    if (relationship === 'Other') {
      if (otherOptions.length === 1) {
        preloadPerson(otherOptions[0], 'Other');
      } else {
        clearPersonForRelationship('Other');
      }
      setSelectedChildKey('NEW_CHILD');
      return;
    }

    clearPersonForRelationship(relationship);
    setSelectedChildKey('NEW_CHILD');
  };

  const handleChildSelectionChange = (value: string) => {
    setSelectedChildKey(value);
    if (value === 'NEW_CHILD') {
      clearPersonForRelationship('Child');
      return;
    }
    const child = childrenOptions.find((item) => personKey(item) === value);
    if (child) {
      preloadPerson(child, 'Child');
    }
  };

  const handleClose = () => {
    setFormData(emptyPatientData);
    setChannelizationClearedWarning(false);
    setAssignmentModalOpen(false);
    onClose();
  };

  const handleAssignSpecialist = (specialist: Specialist) => {
    setFormData((prev) => ({
      ...prev,
      assignedSpecialistId: specialist.specialistId,
      assignedSpecialistName: specialist.fullName,
      assignmentDate: new Date().toISOString()
    }));
    setChannelizationClearedWarning(false);
    setAssignmentModalOpen(false);
  };

  const canOpenChannelization =
    !isReadOnly && formData.serviceType.trim() !== '';
  const hasAssignedSpecialist =
    typeof formData.assignedSpecialistId === 'number' && formData.assignedSpecialistId > 0;
  const serviceTypeDescription =
    serviceTypeOptions.find((item) => item.code === formData.serviceType)?.description ||
    formData.serviceType;

  const handleSave = async () => {
    try {
      if (patientAgeError) return;
      const ageValue = (formData.age || '').trim();
      const computedAgeRange = getAgeRangeFromAge(ageValue, ageRangeOptions) || formData.ageRange || '';
      const payload: PatientCaptureData = {
        ...formData,
        age: ageValue,
        ageRange: computedAgeRange,
        otherRelationshipDescription:
          formData.relationship === 'Other'
            ? (formData.otherRelationshipDescription || '').trim()
            : ''
      };
      await Promise.resolve(onSave(payload));
      setFormData(emptyPatientData);
      setChannelizationClearedWarning(false);
    } catch {
      // El padre muestra el error (p. ej. validationError o alerta)
    }
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isReadOnly ? 'Detalle del servicio' : 'Captura del Servicio'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {!isReadOnly && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Los campos con asterisco (*) son obligatorios para guardar el servicio. Los que indican «(opcional)» no lo
              son.
            </Typography>
          )}
          {(() => {
            const specialistLabel = formData.assignedSpecialistName?.trim() || '';
            const assignmentDateLabel = formData.assignmentDate
              ? new Date(formData.assignmentDate).toLocaleString('es-MX')
              : null;

            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  px: 2,
                  py: 1.5,
                  border: '1px solid',
                  borderColor: hasAssignedSpecialist ? 'success.light' : 'divider',
                  borderRadius: 1.5,
                  bgcolor: hasAssignedSpecialist ? 'success.50' : 'background.paper',
                  borderLeft: '3px solid',
                  borderLeftColor: hasAssignedSpecialist ? 'success.main' : BRAND_COLORS.mediumPurple
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: hasAssignedSpecialist ? 'success.main' : BRAND_COLORS.lightLavender,
                      color: hasAssignedSpecialist ? 'common.white' : BRAND_COLORS.darkPurple
                    }}
                  >
                    {hasAssignedSpecialist ? (
                      <AssignmentIndOutlinedIcon fontSize="small" />
                    ) : (
                      <PersonOutlineOutlinedIcon fontSize="small" />
                    )}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Canalización
                      </Typography>
                      <Chip
                        size="small"
                        label={hasAssignedSpecialist ? 'Asignado' : 'Opcional'}
                        color={hasAssignedSpecialist ? 'success' : 'default'}
                        variant={hasAssignedSpecialist ? 'filled' : 'outlined'}
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    </Box>
                    {hasAssignedSpecialist ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {specialistLabel || `Especialista #${formData.assignedSpecialistId}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {assignmentDateLabel
                            ? `Asignado el ${assignmentDateLabel}`
                            : 'Fecha de asignación no disponible'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Sin especialista. Puedes asignarlo ahora o más tarde.
                      </Typography>
                    )}
                  </Box>
                </Box>
                {!isReadOnly && (
                  <Tooltip
                    title={
                      canOpenChannelization
                        ? hasAssignedSpecialist
                          ? 'Cambiar especialista asignado'
                          : 'Asignar especialista a este servicio'
                        : 'Selecciona primero el tipo de servicio'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant={hasAssignedSpecialist ? 'text' : 'contained'}
                        startIcon={<AssignmentIndOutlinedIcon />}
                        disabled={!canOpenChannelization}
                        onClick={() => setAssignmentModalOpen(true)}
                        sx={
                          hasAssignedSpecialist
                            ? undefined
                            : {
                                bgcolor: BRAND_COLORS.darkPurple,
                                '&:hover': { bgcolor: '#35375a' }
                              }
                        }
                      >
                        {hasAssignedSpecialist ? 'Cambiar' : 'Canalizar'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            );
          })()}
          {isReadOnly && (
            <Alert severity="info">
              Vista solo consulta de lo capturado. No puedes modificar estos datos desde aquí.
            </Alert>
          )}
          {!isReadOnly && validationError && <Alert severity="error">{validationError}</Alert>}
          {!isReadOnly && channelizationClearedWarning && (
            <Alert severity="warning" onClose={() => setChannelizationClearedWarning(false)}>
              Al cambiar el tipo de servicio se quitará la canalización actual. Debes volver a canalizar el
              servicio con un especialista compatible después de guardar.
            </Alert>
          )}
          {!isReadOnly && lockEmployeeIdentityFields && (
            <Alert severity="info">
              Los datos personales del empleado (nombre, correo, teléfono, edad y género) provienen del registro y no
              se pueden modificar aquí. Solo puedes indicar el tipo de servicio y cómo se enteró del programa.
            </Alert>
          )}
          {initialData?.draftReferralId && (
            <Alert severity="info">
              <Typography variant="body2">
                Número de caso: {initialData.draftReferralId} | Referral:{' '}
                {initialData.externalReferralId ?? 'Pendiente'}
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required disabled={isReadOnly}>
                    <InputLabel>¿Para quién es el servicio?</InputLabel>
                    <Select
                      label="¿Para quién es el servicio?"
                      value={formData.relationship}
                      onChange={(e) => handleRelationshipChange(e.target.value)}
                    >
                      <MenuItem value="Employee">Empleado</MenuItem>
                      <MenuItem value="Partner">Pareja</MenuItem>
                      <MenuItem value="Child">Hijo(a)</MenuItem>
                      <MenuItem value="Other">Otro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formData.relationship === 'Child' && childrenOptions.length >= 1 && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={isReadOnly}>
                      <InputLabel>¿Cuál hijo? (opcional)</InputLabel>
                      <Select
                        label="¿Cuál hijo? (opcional)"
                        value={selectedChildKey}
                        onChange={(e) => handleChildSelectionChange(e.target.value)}
                      >
                        <MenuItem value="NEW_CHILD">Nuevo registro de hijo</MenuItem>
                        {childrenOptions.map((child) => {
                          const key = personKey(child);
                          return (
                            <MenuItem key={key} value={key}>
                              {`${child.firstName} ${child.lastName} ${child.middleName}`.replace(/\s+/g, ' ').trim()}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {formData.relationship === 'Other' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Relación con el empleado"
                      placeholder="Ej. Hermano, tío, amigo…"
                      value={formData.otherRelationshipDescription || ''}
                      onChange={(e) => handleChange('otherRelationshipDescription', e.target.value)}
                      disabled={isReadOnly}
                    />
                  </Grid>
                )}
                {!(formData.relationship === 'Child' && childrenOptions.length >= 1) &&
                  formData.relationship !== 'Other' && <Grid item xs={12} md={6} />}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Nombre"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    disabled={fieldDisabled(lockEmployeeIdentityFields)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Apellido Paterno"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    disabled={fieldDisabled(lockEmployeeIdentityFields)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Apellido Materno (opcional)"
                    value={formData.middleName}
                    onChange={(e) => handleChange('middleName', e.target.value)}
                    disabled={fieldDisabled(lockEmployeeIdentityFields)}
                  />
                </Grid>
                {formData.relationship === 'Employee' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Correo"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      disabled={fieldDisabled(lockEmployeeIdentityFields)}
                      helperText={
                        isReadOnly || lockEmployeeIdentityFields ? 'Datos del empleado.' : undefined
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={formData.relationship === 'Employee' ? 6 : 12}>
                  <TextField
                    fullWidth
                    label="Celular (opcional)"
                    value={formData.mobilePhone}
                    onChange={(e) =>
                      handleChange('mobilePhone', e.target.value.replace(/\D/g, '').slice(0, 10))
                    }
                    disabled={fieldDisabled(lockEmployeeIdentityFields)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Edad (opcional)"
                    value={formData.age || ''}
                    onChange={(e) =>
                      handleChange('age', e.target.value.replace(/\D/g, '').slice(0, 2))
                    }
                    disabled={fieldDisabled(lockEmployeeIdentityFields)}
                    inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                    error={patientAgeError}
                    helperText={
                      patientAgeError
                        ? 'Ingresa una edad válida (1 o 2 dígitos).'
                        : lockEmployeeIdentityFields
                          ? 'Tomada de los datos del empleado.'
                          : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth disabled={fieldDisabled(lockEmployeeIdentityFields)}>
                    <InputLabel>Género (opcional)</InputLabel>
                    <Select
                      label="Género (opcional)"
                      value={formData.gender || ''}
                      onChange={(e) => handleChange('gender', e.target.value)}
                    >
                      {genderOptions.map((item) => (
                        <MenuItem key={item.code} value={item.code}>
                          {item.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required disabled={isReadOnly || lockServiceType}>
                    <InputLabel>Tipo de servicio</InputLabel>
                    <Select
                      label="Tipo de servicio"
                      value={formData.serviceType}
                      onChange={(e) => handleServiceTypeChange(e.target.value)}
                    >
                      {serviceTypeOptions.map((item) => (
                        <MenuItem key={item.code} value={item.code}>
                          {item.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required disabled={isReadOnly}>
                    <InputLabel>Método de entrega</InputLabel>
                    <Select
                      label="Método de entrega"
                      value={formData.deliveryMethod}
                      onChange={(e) => handleChange('deliveryMethod', e.target.value)}
                    >
                      {deliveryMethodOptions.map((item) => (
                        <MenuItem key={item.code} value={item.code}>
                          {item.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.serviceType === 'EAP' && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={Boolean(formData.hasPriorPsychologicalTreatment)}
                          disabled={isReadOnly}
                          onChange={(e) =>
                            handleChange('hasPriorPsychologicalTreatment', e.target.checked)
                          }
                        />
                      }
                      label="¿El paciente ha recibido tratamiento psicológico anterior? (opcional)"
                    />
                  </Grid>
                )}
                {isHwbWellBeingCoachingServiceType(formData.serviceType) && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={Boolean(formData.pregnancyCoaching)}
                          disabled={isReadOnly}
                          onChange={(e) => handleChange('pregnancyCoaching', e.target.checked)}
                        />
                      }
                      label="Coaching de embarazo (pregnancy) (opcional)"
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 2,
                  height: '100%'
                }}
              >
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    PRE DIAGNOSTICO
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    OBSERVACIONES, RECUERDA QUE ESTE CAMPO DEBERAS USARLO PARA DATOS DESTACADOS DE LA LLAMADA COMO:
                    QUE ES UN USUARIO RECURRENTE, QUE DETECTES QUE EL BENEFICIARIO NO VIVE EN EL MISMO DOMICILIO,
                    QUE PIDA CON UN DETERMINADO ESPECIALISTA, ETC.
                  </Typography>
                  <TextField
                    multiline
                    minRows={7}
                    maxRows={7}
                    fullWidth
                    label="Observaciones del caso (opcional)"
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '220px',
                        alignItems: 'flex-start'
                      },
                      '& .MuiInputBase-inputMultiline': {
                        height: '100% !important',
                        overflowY: 'auto !important'
                      }
                    }}
                    placeholder="Escribe aquí las observaciones del caso..."
                    value={formData.preDiagnosticNotes || ''}
                    onChange={(e) => handleChange('preDiagnosticNotes', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>

        </Stack>
      </DialogContent>
      <DialogActions>
        {isReadOnly ? (
          <Button variant="contained" onClick={handleClose}>
            Cerrar
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave} disabled={!canSave || patientAgeError}>
              Guardar servicio
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    <ServiceSpecialistAssignmentModal
      open={assignmentModalOpen}
      onClose={() => setAssignmentModalOpen(false)}
      serviceType={formData.serviceType}
      serviceTypeDescription={serviceTypeDescription}
      deliveryMethod={formData.deliveryMethod || null}
      deliveryMethodOptions={deliveryMethodOptions}
      selectedSpecialistId={formData.assignedSpecialistId ?? null}
      onAssign={handleAssignSpecialist}
    />
    </>
  );
};

export default CapturePatientModal;
